import type { ReactElement, Ref, TextareaHTMLAttributes } from 'react'
import { useId } from 'react'
import { cn } from '@/lib/cn'

export type TextareaTone = 'default' | 'accent' | 'success' | 'warning' | 'danger' | 'muted'

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string
  hint?: string
  error?: string
  /** Tints the field so state (approved, failed, untouched…) reads at a glance. */
  tone?: TextareaTone
  ref?: Ref<HTMLTextAreaElement>
}

const tones: Record<TextareaTone, string> = {
  default:
    'border-line bg-surface hover:border-line-strong focus:border-accent focus:ring-accent/30 dark:bg-sunken',
  accent:
    'border-brand-400/50 bg-accent-soft/50 focus:border-accent focus:ring-accent/30 dark:border-brand-500/40',
  success:
    'border-ok-500/50 bg-ok-100/60 focus:border-ok-500 focus:ring-ok-500/25 dark:border-ok-500/35 dark:bg-ok-950/50',
  warning:
    'border-warn-500/50 bg-warn-100/60 focus:border-warn-500 focus:ring-warn-500/25 dark:border-warn-500/35 dark:bg-warn-950/50',
  danger:
    'border-bad-500/55 bg-bad-100/60 focus:border-bad-500 focus:ring-bad-500/25 dark:border-bad-500/40 dark:bg-bad-950/50',
  muted: 'border-line bg-sunken text-fg-muted focus:border-accent focus:ring-accent/30 dark:bg-canvas'
}

export function Textarea({
  label,
  hint,
  error,
  tone = 'default',
  className,
  id,
  'aria-describedby': describedBy,
  ...rest
}: TextareaProps): ReactElement {
  const generatedId = useId()
  const textareaId = id ?? `textarea-${generatedId}`
  const hintId = `${textareaId}-hint`
  const errorId = `${textareaId}-error`
  const invalid = Boolean(error)
  const showHint = Boolean(hint) && !invalid
  const description =
    [describedBy, showHint ? hintId : null, invalid ? errorId : null].filter(Boolean).join(' ') ||
    undefined

  return (
    <div className="w-full">
      {label ? (
        <label htmlFor={textareaId} className="mb-1.5 block text-sm font-medium text-fg">
          {label}
        </label>
      ) : null}
      <textarea
        id={textareaId}
        aria-invalid={invalid || undefined}
        aria-describedby={description}
        className={cn(
          'block min-h-20 w-full resize-y rounded-control border px-3 py-2.5 text-sm leading-relaxed text-fg shadow-sheet transition-colors',
          'placeholder:text-fg-subtle focus:outline-none focus:ring-2',
          'disabled:cursor-not-allowed disabled:bg-sunken disabled:text-fg-subtle disabled:opacity-70',
          invalid ? tones.danger : tones[tone],
          className
        )}
        {...rest}
      />
      {showHint ? (
        <p id={hintId} className="mt-1.5 text-xs text-fg-muted">
          {hint}
        </p>
      ) : null}
      {invalid ? (
        <p id={errorId} className="mt-1.5 text-xs font-medium text-bad-600 dark:text-bad-300">
          {error}
        </p>
      ) : null}
    </div>
  )
}
